<script setup lang="ts">
  import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
  import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
  import { useRapportenStore } from "../stores/rapporten";
  import { useHerstelSubmission } from "../composables/useHerstelSubmission";
  import { isDocumentGenerationTimeout } from "../composables/useGeneratedDocument";
  import { useToast } from "../composables/useToast";
  import { useConfirm } from "primevue/useconfirm";
  import { captureError } from "../lib/sentry";
  import { supabase } from "../lib/supabase";
  import { resizeImageFile } from "../utils/imageUtils";
  import { validateUploadedFile } from "../utils/fileUtils";
  import { getInspectieTypeParam, getPreferredInspectieType, getRapportDetailsPath } from "../utils/rapportScope";

  // Components
  import AppHeader from "../components/AppHeader.vue";
  import Constatering from "../components/Constatering.vue";
  import HerstelInstructies from "../components/HerstelInstructies.vue";
  import SignaturePad from "../components/SignaturePad.vue";
  import InputField from "../components/InputField.vue";

  // PrimeVue
  import Button from "primevue/button";
  import ProgressSpinner from "primevue/progressspinner";

  // Types
  import {
    emptyInstallateurGegevens,
    inspectieTypeLabels,
    type HerstelSubmissionMethod,
    type HerstelSubmissionUpsert,
    type InspectieType,
    type InstallateurGegevens,
  } from "../../../../types";

  const route = useRoute();
  const router = useRouter();
  const rapportenStore = useRapportenStore();
  const toast = useToast();
  const confirm = useConfirm();
  const routeRapportId = computed(() => String(route.params.id || ""));
  const activeInspectieType = computed(() => getInspectieTypeParam(route.params.type));

  // Deep-link focus: scroll to and highlight a specific constatering when the
  // magic-link lands here with ?focus=<constateringId>.
  let hasFocused = false;
  const focusConstatering = (constateringId: string, attempt = 0) => {
    const el = document.getElementById(constateringId);
    if (!el) {
      // Constateringen may not be rendered yet; retry a few times.
      if (attempt < 10) {
        setTimeout(() => focusConstatering(constateringId, attempt + 1), 200);
      }
      return;
    }
    hasFocused = true;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("constatering-focus-highlight");
    setTimeout(() => el.classList.remove("constatering-focus-highlight"), 2600);
  };

  const scheduleFocus = () => {
    if (hasFocused) return;
    const focusId = String(route.query.focus || "");
    if (!focusId) return;
    void nextTick(() => focusConstatering(focusId));
  };

  // Fetch rapport
  onMounted(async () => {
    const data = await rapportenStore.fetchRapportById(routeRapportId.value);
    if (!data) {
      router.push("/rapporten");
      return;
    }
    void loadHerstelState();
    scheduleFocus();
  });

  watch(routeRapportId, (nextId, previousId) => {
    if (!nextId || nextId === previousId) return;
    void rapportenStore.fetchRapportById(nextId);
    void loadHerstelState();
  });

  watch(activeInspectieType, (nextType, previousType) => {
    if (!nextType || nextType === previousType) return;
    void loadHerstelState();
  });

  onUnmounted(() => {
    rapportenStore.unsubscribeFromRapport();
  });

  const rapport = computed(() => rapportenStore.currentRapport);

  // ── Unsaved constatering tracking ──────────────────────────────────
  // Each Constatering card reports its dirty state here, and exposes a
  // flushSave() we call before submitting so no in-progress herstel work is lost.
  type DirtyInfo = { volgnummer: number | null; canSave: boolean };
  type CardInstance = { flushSave: () => Promise<{ status: "clean" | "invalid" | "saved" | "error" }> };
  const dirtyCards = reactive(new Map<string, DirtyInfo>());
  const cardRefs = new Map<string, CardInstance>();

  const registerCardRef = (id: string, el: unknown) => {
    if (el) cardRefs.set(id, el as CardInstance);
    else cardRefs.delete(id);
  };

  // Stable per-id ref setter so the template :ref isn't a fresh closure each render.
  const cardRefSetters = new Map<string, (el: unknown) => void>();
  const cardRefSetter = (id: string) => {
    let setter = cardRefSetters.get(id);
    if (!setter) {
      setter = (el: unknown) => registerCardRef(id, el);
      cardRefSetters.set(id, setter);
    }
    return setter;
  };

  const handleDirtyChange = (payload: { id: string; volgnummer: number | null; dirty: boolean; canSave: boolean }) => {
    if (payload.dirty) {
      dirtyCards.set(payload.id, { volgnummer: payload.volgnummer, canSave: payload.canSave });
    } else {
      dirtyCards.delete(payload.id);
    }
  };

  // Count of unsaved cards that CAN be saved (will be flushed automatically on submit).
  const autoSaveCount = computed(() => Array.from(dirtyCards.values()).filter((c) => c.canSave).length);

  // Warn before leaving (refresh/close or in-app navigation) with unsaved work.
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (dirtyCards.size > 0) {
      event.preventDefault();
      event.returnValue = "";
    }
  };
  onMounted(() => window.addEventListener("beforeunload", handleBeforeUnload));
  onUnmounted(() => window.removeEventListener("beforeunload", handleBeforeUnload));
  onBeforeRouteLeave(() => {
    if (dirtyCards.size === 0) return true;
    return window.confirm(
      "U heeft niet-opgeslagen herstelwijzigingen. Als u de pagina verlaat gaan deze verloren. Toch verlaten?",
    );
  });

  // Persist all unsaved, valid cards. Returns false if any could not be saved.
  const flushDirtyCards = async (): Promise<boolean> => {
    const ids = Array.from(dirtyCards.keys());
    let allSaved = true;
    for (const id of ids) {
      const card = cardRefs.get(id);
      if (!card?.flushSave) continue;
      const result = await card.flushSave();
      if (result.status === "error" || result.status === "invalid") {
        allSaved = false;
      }
    }
    return allSaved;
  };

  const selectedScopeLabel = computed(() => {
    if (!activeInspectieType.value) return "";
    return inspectieTypeLabels[activeInspectieType.value as InspectieType] || activeInspectieType.value;
  });

  const activeInspectieEntry = computed(() => {
    if (!activeInspectieType.value || !Array.isArray(rapport.value?.inspectieStatus)) return null;
    return rapport.value.inspectieStatus.find((entry) => entry.type === activeInspectieType.value) || null;
  });

  const rapportTitle = computed(() => {
    return selectedScopeLabel.value ? `Herstelverklaring ${selectedScopeLabel.value}` : "Herstelverklaring";
  });

  // ── Herstel submission (installateur gegevens + indiening) ─────────
  // Stored in public.herstel_submissions, one row per rapport + inspectie type.
  const {
    submission,
    saving: savingSubmission,
    generatingForm,
    loadSubmission,
    saveSubmission,
    downloadHerstelFormulier,
  } = useHerstelSubmission();

  // Local draft of the submission so the form is editable before a row exists.
  const submissionMethod = ref<HerstelSubmissionMethod | null>(null);
  const installateurForm = reactive<InstallateurGegevens>(emptyInstallateurGegevens());
  const handtekeningUrl = ref<string | null>(null);
  const uploadUrl = ref<string | null>(null);

  const applySubmissionToDraft = () => {
    const current = submission.value;
    submissionMethod.value = current?.submission_method ?? null;
    Object.assign(installateurForm, emptyInstallateurGegevens(), current?.installateur ?? {});
    handtekeningUrl.value = current?.handtekening_url ?? null;
    uploadUrl.value = current?.upload_url ?? null;
    isEdited.value = false;
  };

  const loadHerstelState = async () => {
    if (!routeRapportId.value || !activeInspectieType.value) return;
    await loadSubmission(routeRapportId.value, activeInspectieType.value);
    applySubmissionToDraft();
  };

  const buildUpsertPayload = (extra: Partial<HerstelSubmissionUpsert> = {}): HerstelSubmissionUpsert => ({
    rapport_id: routeRapportId.value,
    inspectie_type: activeInspectieType.value as InspectieType,
    submission_method: submissionMethod.value,
    installateur: { ...installateurForm },
    handtekening_url: handtekeningUrl.value,
    upload_url: uploadUrl.value,
    ...extra,
  });

  const isSubmitted = computed(() => Boolean(submission.value?.submitted_at));

  const ensureValidActiveScope = () => {
    if (!rapport.value) return;

    const hasMatchingType =
      activeInspectieType.value &&
      Array.isArray(rapport.value.inspectieStatus) &&
      rapport.value.inspectieStatus.some((entry) => entry.type === activeInspectieType.value);

    if (hasMatchingType) return;

    const fallbackType = getPreferredInspectieType(rapport.value.inspectieStatus);
    if (!fallbackType) {
      router.replace("/rapporten");
      return;
    }

    router.replace(`/rapport/${rapport.value.id}/${fallbackType}/herstel`);
  };

  watch([rapport, activeInspectieType], ensureValidActiveScope, { immediate: true });

  // Filtered constateringen for selected scope
  const filteredConstateringen = computed(() => {
    if (!activeInspectieType.value || !rapport.value?.constateringen) return [];
    const constateringen = rapport.value.constateringen;
    if (!Array.isArray(constateringen)) return [];

    return constateringen.filter((c: any) => {
      if (!c.inspecties || !Array.isArray(c.inspecties)) return false;
      return c.inspecties.includes(activeInspectieType.value);
    });
  });

  const hasHerinspectie = computed(() => {
    return filteredConstateringen.value.some((constatering: any) => constatering.vereist_herinspectie);
  });

  // Grouping by Location
  const groupedConstateringen = computed(() => {
    const groups: Record<string, any[]> = {};
    const noLocationId = "no-location";

    // Sort rejected constateringen first within each group
    const sorted = [...filteredConstateringen.value].sort((a: any, b: any) => {
      if (a.herstelStatus === "rejected" && b.herstelStatus !== "rejected") return -1;
      if (a.herstelStatus !== "rejected" && b.herstelStatus === "rejected") return 1;
      return 0;
    });

    sorted.forEach((c: any) => {
      const locId = c.internal_locatie_id || noLocationId;
      if (!groups[locId]) {
        groups[locId] = [];
      }
      groups[locId].push(c);
    });

    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      if (a === noLocationId) return 1;
      if (b === noLocationId) return -1;
      const locA = groups[a][0]?.locatie || "";
      const locB = groups[b][0]?.locatie || "";
      return locA.localeCompare(locB);
    });

    return sortedGroupKeys.map((key) => ({
      id: key,
      name: key === noLocationId ? "Geen locatie" : groups[key][0]?.locatie || "Onbekende locatie",
      items: groups[key],
    }));
  });

  // Stats for the selected scope
  const constateringenStats = computed(() => {
    const stats = { total: 0, hersteld: 0, bevestigd: 0, opmerkingen: 0, afgekeurd: 0 };
    for (const c of filteredConstateringen.value as any[]) {
      if (c.constatering) {
        stats.total++;
        if (c.hersteld) stats.hersteld++;
        if (c.herstelStatus === "accepted") stats.bevestigd++;
        if (c.herstelStatus === "rejected") stats.afgekeurd++;
      } else {
        stats.opmerkingen++;
      }
    }
    return stats;
  });

  const isEdited = ref(false);

  const handleInstallatorUpdate = () => {
    isEdited.value = true;
  };

  // Persist the current draft (installateur gegevens, method, handtekening/upload).
  // Failures are toasted (and reported to Sentry) by the composable.
  const saveInstallateurGegevens = async (extra: Partial<HerstelSubmissionUpsert> = {}): Promise<boolean> => {
    const saved = await saveSubmission(buildUpsertPayload(extra));
    if (saved) {
      isEdited.value = false;
      return true;
    }
    return false;
  };

  const handleSaveClick = async () => {
    const saved = await saveInstallateurGegevens();
    if (saved) {
      toast.success("Installateur gegevens opgeslagen");
    }
  };

  // Submission method selection — persisted immediately so the choice survives a refresh
  const selectSubmissionMethod = async (method: HerstelSubmissionMethod) => {
    submissionMethod.value = method;
    await saveInstallateurGegevens();
  };

  const switchSubmissionMethod = async () => {
    if (submissionMethod.value === "digital") {
      submissionMethod.value = "manual";
      Object.assign(installateurForm, emptyInstallateurGegevens());
      handtekeningUrl.value = null;
    } else {
      submissionMethod.value = "digital";
      uploadUrl.value = null;
    }
    await saveInstallateurGegevens();
  };

  // Handle constatering updates (local state only — DB persistence is handled by Constatering.vue directly)
  const handleConstateringUpdate = (updatedConstatering: any, _index: number) => {
    if (!rapport.value?.constateringen) return;
    const fullIndex = (rapport.value.constateringen as any[]).findIndex((c: any) => c.id === updatedConstatering.id);
    if (fullIndex !== -1) {
      (rapport.value.constateringen as any[])[fullIndex] = updatedConstatering;
    }
  };

  // Resolve a userFiles storage path from its public URL (for removal)
  const storagePathFromPublicUrl = (url: string): string | null => {
    const marker = "/userFiles/";
    const index = url.indexOf(marker);
    if (index === -1) return null;
    return decodeURIComponent(url.slice(index + marker.length).split("?")[0]);
  };

  // Signature handling — uploaded to storage and persisted on the submission immediately
  const handleSignature = async (signatureData: string) => {
    if (!rapport.value) return;
    try {
      const signatureBlob = await fetch(signatureData).then((res) => res.blob());
      const filePath = `rapporten/${rapport.value.id}/handtekening${activeInspectieType.value}.png`;

      const { error: uploadError } = await supabase.storage
        .from("userFiles")
        .upload(filePath, signatureBlob, { contentType: "image/png", upsert: true });

      if (uploadError) throw new Error("Handtekening uploaden mislukt: " + uploadError.message);

      const {
        data: { publicUrl },
      } = supabase.storage.from("userFiles").getPublicUrl(filePath);

      handtekeningUrl.value = publicUrl;
      const saved = await saveInstallateurGegevens();
      if (saved) {
        toast.success("Handtekening succesvol opgeslagen");
      }
    } catch (error: any) {
      console.error("Error saving signature:", error);
      toast.error(error.message || "Handtekening opslaan mislukt", error);
    }
  };

  const clearSignature = async () => {
    if (!handtekeningUrl.value || !rapport.value) return;

    try {
      const filePath = `rapporten/${rapport.value.id}/handtekening${activeInspectieType.value}.png`;
      await supabase.storage.from("userFiles").remove([filePath]);
      handtekeningUrl.value = null;
      const saved = await saveInstallateurGegevens();
      if (saved) {
        toast.success("Handtekening succesvol verwijderd");
      }
    } catch (error: any) {
      console.error("Error clearing signature:", error);
      toast.error(error.message || "Handtekening verwijderen mislukt", error);
    }
  };

  // File upload for manual submission
  const handleFileUpload = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const rawFile = target.files?.[0];
    if (!rawFile || !rapport.value) return;

    try {
      // Validate size + type up front. This flow legitimately accepts a signed
      // PDF or a photo, so images are allowed alongside PDF.
      const rawValidation = validateUploadedFile(rawFile, { allowImages: true });
      if (!rawValidation.ok) {
        toast.error(rawValidation.error, undefined, { report: false });
        target.value = "";
        return;
      }

      // Downscale/recompress images before upload; leave PDFs untouched.
      const file = await resizeImageFile(rawFile);
      // Re-derive the SAFE extension/contentType from the (possibly transcoded)
      // result via the allow-list — never trust file.name (path injection) or
      // file.type (spoofed Content-Type on the public bucket) verbatim.
      const validation = validateUploadedFile(file, { allowImages: true });
      const safe = validation.ok ? validation.value : rawValidation.value;
      // UUID-prefixed path so a crafted file.name can never inject `../` segments.
      const filePath = `rapporten/${rapport.value.id}/upload-${activeInspectieType.value}-${crypto.randomUUID()}.${safe.extension}`;

      const { error: uploadError } = await supabase.storage
        .from("userFiles")
        .upload(filePath, file, { upsert: true, contentType: safe.contentType });

      if (uploadError) throw new Error("Document uploaden mislukt: " + uploadError.message);

      const {
        data: { publicUrl },
      } = supabase.storage.from("userFiles").getPublicUrl(filePath);

      uploadUrl.value = publicUrl;
      const saved = await saveInstallateurGegevens();
      if (saved) {
        toast.success("Document succesvol geupload");
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast.error(error.message || "Document uploaden mislukt", error);
    }
  };

  const confirmClearUpload = () => {
    if (!uploadUrl.value) return;
    confirm.require({
      message: "Weet u zeker dat u het geüploade document wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      header: "Document verwijderen",
      icon: "pi pi-exclamation-triangle",
      rejectLabel: "Annuleren",
      acceptLabel: "Verwijderen",
      acceptClass: "p-button-danger",
      accept: () => clearUpload(),
    });
  };

  const clearUpload = async () => {
    if (!uploadUrl.value) return;

    try {
      const filePath = storagePathFromPublicUrl(uploadUrl.value);
      if (filePath) {
        await supabase.storage.from("userFiles").remove([filePath]);
      }
      uploadUrl.value = null;
      const saved = await saveInstallateurGegevens();
      if (saved) {
        toast.success("Document succesvol verwijderd");
      }
    } catch (error: any) {
      console.error("Error clearing upload:", error);
      toast.error(error.message || "Document verwijderen mislukt", error);
    }
  };

  // Download the printable installateur form (generated by the printer service)
  const downloadForm = async () => {
    if (!rapport.value || !activeInspectieType.value) return;
    try {
      const fileName = `herstelverklaring-installateur-${rapport.value.inspectieRapportNr || rapport.value.id}-${activeInspectieType.value}.pdf`;
      await downloadHerstelFormulier(rapport.value.id, activeInspectieType.value, fileName);
    } catch (error: any) {
      console.error("Error downloading form:", error);
      if (isDocumentGenerationTimeout(error)) {
        // Generation continues server-side; the PDF appears under Documenten.
        toast.error(error.message, error, { report: false });
      } else {
        toast.error(error.message || "Downloaden van het formulier is mislukt", error);
      }
    }
  };

  // Validation for submission
  const relevantConstateringen = computed(() =>
    (filteredConstateringen.value as any[]).filter((c: any) => c.constatering),
  );

  // Constateringen that block submission, with the reason why. A card with
  // unsaved-but-valid changes is NOT blocking: it will be auto-saved on submit.
  const blockingConstateringen = computed(() => {
    const blocking: { volgnummer: number | null; reason: "invalid" | "not-hersteld" }[] = [];
    for (const c of relevantConstateringen.value) {
      const dirty = dirtyCards.get(c.id);
      if (dirty && !dirty.canSave) {
        blocking.push({ volgnummer: c.volgnummer ?? null, reason: "invalid" });
        continue;
      }
      if (c.hersteld) continue;
      if (dirty && dirty.canSave) continue; // will be saved automatically on submit
      blocking.push({ volgnummer: c.volgnummer ?? null, reason: "not-hersteld" });
    }
    return blocking;
  });

  const formatVolgnummers = (nums: (number | null)[]) => {
    const valid = nums.filter((n): n is number => typeof n === "number").sort((a, b) => a - b);
    return valid.map((n) => `#${n}`).join(", ");
  };

  const allConstateringenHersteld = computed(() => {
    if (relevantConstateringen.value.length === 0) return false;
    return blockingConstateringen.value.length === 0;
  });

  // Check for rejected constateringen
  const hasRejectedConstateringen = computed(() => {
    return (filteredConstateringen.value as any[]).some((c: any) => c.herstelStatus === "rejected");
  });

  const canSubmitScopeH = computed(() => {
    if (hasRejectedConstateringen.value) {
      return {
        valid: false,
        reason:
          "Er zijn afgekeurde constateringen. Dien deze eerst opnieuw in voordat u de herstelverklaring kunt indienen.",
      };
    }
    if (relevantConstateringen.value.length === 0) {
      return { valid: false, reason: `Er zijn geen constateringen om in te dienen voor ${selectedScopeLabel.value}.` };
    }
    if (!allConstateringenHersteld.value) {
      const nums = formatVolgnummers(blockingConstateringen.value.map((b) => b.volgnummer));
      const hasInvalid = blockingConstateringen.value.some((b) => b.reason === "invalid");
      return {
        valid: false,
        reason: hasInvalid
          ? `Constatering(en) ${nums} hebben onvolledige wijzigingen. Voeg een foto én opmerking toe en sla op.`
          : `Lever nog herstelbewijs aan voor constatering(en) ${nums}.`,
      };
    }

    if (submissionMethod.value === "digital") {
      const requiredFields: (keyof InstallateurGegevens)[] = [
        "Bedrijfsnaam",
        "NaamVerantwoordelijke",
        "Functie",
        "Certificatiestatus",
        "Adres",
        "PostcodeenPlaats",
        "Emailadres",
        "Telnr",
      ];
      const missing = requiredFields.find((field) => !installateurForm[field]?.trim());
      if (missing) return { valid: false, reason: `${missing} is vereist` };
      if (!handtekeningUrl.value) return { valid: false, reason: "Handtekening is vereist" };
    } else if (submissionMethod.value === "manual") {
      if (!uploadUrl.value) return { valid: false, reason: "Upload het ingevulde formulier" };
    } else {
      return { valid: false, reason: "Kies een methode voor de installateur gegevens" };
    }

    // Check if already submitted
    if (isSubmitted.value) {
      return { valid: false, reason: "Herstelverklaring is al ingediend. Wacht op goedkeuring van de inspecteur." };
    }

    return { valid: true, reason: "" };
  });

  const markScopeComplete = async () => {
    if (!rapport.value) return;

    // Bail before mutating anything if submission is blocked by something a save
    // cannot resolve (rejected/incomplete constateringen, missing installateur
    // gegevens, already submitted). Dirty-but-valid cards do not block validity,
    // so a valid state means the only pending writes are ones we want to flush.
    if (!canSubmitScopeH.value.valid) {
      toast.error(canSubmitScopeH.value.reason);
      return;
    }

    // Persist any unsaved (but valid) constatering edits so nothing the
    // opdrachtgever filled in is silently lost on submit.
    const flushed = await flushDirtyCards();
    if (!flushed) {
      toast.error("Niet alle herstelwijzigingen konden worden opgeslagen. Controleer de gemarkeerde constateringen.");
      return;
    }

    try {
      if (!activeInspectieType.value) {
        throw new Error("Inspectie type ontbreekt voor de herstelverklaring");
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Persist the draft and the formal submission in one write.
      const saved = await saveSubmission(
        buildUpsertPayload({
          submitted_at: new Date().toISOString(),
          submitted_by: user?.id ?? null,
        }),
      );
      if (!saved) {
        throw new Error("De herstelverklaring kon niet worden opgeslagen");
      }

      const { error: notificationError } = await supabase.rpc("enqueue_herstel_submission_notification", {
        p_rapport_id: rapport.value.id,
        p_inspectie_type: activeInspectieType.value,
      });

      if (notificationError) {
        // The submission itself succeeded; the inspecteur just won't get an
        // automatic notification. Don't fail the user flow, but make sure we
        // hear about it in Sentry.
        console.error("Error enqueuing herstel submission notification:", notificationError);
        captureError(notificationError, {
          stage: "enqueue_herstel_submission_notification",
          rapport_id: rapport.value.id,
          inspectie_type: activeInspectieType.value,
        });
      }

      toast.success(`Herstelverklaring ${selectedScopeLabel.value} is succesvol ingediend`);
    } catch (error: any) {
      console.error("Error submitting declaration:", error);
      toast.error(error?.message || "Er is een fout opgetreden bij het indienen van de herstelverklaring", error);
    }
  };

  const formatSubmitDate = (dateString: string) => {
    return new Intl.DateTimeFormat("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  };
</script>

<template>
  <div class="herstel-page">
    <AppHeader
      :title="rapportTitle"
      :show-back-button="true"
      :back-to="rapport && activeInspectieType ? getRapportDetailsPath(rapport.id, activeInspectieType) : '/rapporten'"
      back-text="Terug naar rapport"
    />

    <!-- Loading -->
    <div
      v-if="rapportenStore.loading"
      class="status-card status-loading"
    >
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <!-- Error -->
    <div
      v-else-if="rapportenStore.error"
      class="status-card status-error"
    >
      {{ rapportenStore.error }}
    </div>

    <!-- Not found -->
    <div
      v-else-if="!rapport"
      class="status-card"
    >
      Rapport niet gevonden.
    </div>

    <div
      v-else-if="!activeInspectieEntry"
      class="status-card"
    >
      Inspectie type niet gevonden.
    </div>

    <template v-else>
      <!-- Rapport info -->
      <div class="rapport-info">
        <span class="rapport-nr">{{ rapport.inspectieRapportNr || "Geen nummer" }}</span>
      </div>
      <!-- Instructions (hidden once ingediend or when there is nothing to herstel) -->
      <HerstelInstructies
        v-if="!isSubmitted && constateringenStats.total > 0"
        variant="herstel"
        :scope-label="selectedScopeLabel"
        :has-herinspectie="hasHerinspectie"
      />
      <!-- Progress Tracker -->
      <section class="detail-section">
          <div class="progress-tracker">
            <div class="progress-item">
              <span class="progress-count">{{ constateringenStats.total }}</span>
              <span class="progress-label">Constateringen</span>
            </div>
            <span class="progress-separator">/</span>
            <div class="progress-item">
              <span
                class="progress-count"
                :class="{ highlight: constateringenStats.hersteld > 0 }"
                >{{ constateringenStats.hersteld }}</span
              >
              <span class="progress-label">Hersteld</span>
            </div>
            <span class="progress-separator">/</span>
            <div class="progress-item">
              <span
                class="progress-count"
                :class="{ highlight: constateringenStats.bevestigd > 0 }"
                >{{ constateringenStats.bevestigd }}</span
              >
              <span class="progress-label">Bevestigd</span>
            </div>
            <template v-if="constateringenStats.afgekeurd > 0">
              <span class="progress-separator">/</span>
              <div class="progress-item">
                <span class="progress-count progress-afgekeurd">{{ constateringenStats.afgekeurd }}</span>
                <span class="progress-label">Afgekeurd</span>
              </div>
            </template>
            <template v-if="constateringenStats.opmerkingen > 0">
              <span class="progress-separator">+</span>
              <div class="progress-item">
                <span class="progress-count progress-opmerking">{{ constateringenStats.opmerkingen }}</span>
                <span class="progress-label">Opmerkingen</span>
              </div>
            </template>
          </div>
      </section>

      <!-- Constateringen List (grouped by location) -->
      <section class="constateringen-section">
          <h3 class="section-title">Constateringen</h3>
          <div
            v-if="groupedConstateringen.length > 0"
            class="constateringen-groups"
          >
            <div
              v-for="group in groupedConstateringen"
              :key="group.id"
              class="location-group"
            >
              <div class="location-header">
                <span class="location-name">{{ group.name }}</span>
                <span class="location-count">({{ group.items.length }})</span>
              </div>
              <div class="constateringen-grid">
                <Constatering
                  v-for="constatering in group.items"
                  :key="constatering.id"
                  :ref="cardRefSetter(constatering.id)"
                  v-model="
                    (rapport.constateringen as any[])[
                      (rapport.constateringen as any[]).findIndex((c: any) => c.id === constatering.id)
                    ]
                  "
                  @update:modelValue="
                    handleConstateringUpdate(
                      $event,
                      (rapport.constateringen as any[]).findIndex((c: any) => c.id === constatering.id),
                    )
                  "
                  @dirty-change="handleDirtyChange"
                  :rapportNr="rapport.inspectieRapportNr ?? ''"
                  :rapportId="rapport.id"
                  :id="constatering.id"
                />
              </div>
            </div>
          </div>
          <p
            v-else
            class="empty-text"
          >
            Geen constateringen gevonden voor dit inspectie type.
          </p>
      </section>

      <!-- Installateur Section -->
      <section class="detail-section">
          <div class="section-header-row">
            <h3 class="section-title">Installateur Gegevens</h3>
            <Button
              v-if="isEdited"
              label="Opslaan"
              icon="pi pi-save"
              size="small"
              :loading="savingSubmission"
              @click="handleSaveClick"
            />
          </div>

          <!-- Method Selection -->
          <div
            v-if="!submissionMethod"
            class="method-selection"
          >
            <p class="method-prompt">Kies een methode om de gegevens in te dienen</p>
            <div class="method-buttons">
              <div
                class="method-btn"
                @click="selectSubmissionMethod('digital')"
              >
                <span class="method-title">Digitaal Formulier</span>
                <span class="method-desc">Vul en onderteken het formulier direct online in</span>
              </div>
              <div
                class="method-btn"
                @click="selectSubmissionMethod('manual')"
              >
                <span class="method-title">Handmatig Document</span>
                <span class="method-desc">Download, print, en upload het getekende document</span>
              </div>
            </div>
          </div>

          <!-- Digital Form -->
          <div
            v-else-if="submissionMethod === 'digital'"
            class="digital-form"
          >
            <div class="method-header">
              <span class="method-label">Digitaal Formulier</span>
              <button
                class="switch-method"
                @click="switchSubmissionMethod"
              >
                Overschakelen naar handmatig document
              </button>
            </div>
            <div class="form-grid">
              <InputField
                v-model="installateurForm.Bedrijfsnaam"
                label="Bedrijfsnaam"
                placeholder="Naam van het bedrijf"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.NaamVerantwoordelijke"
                label="Naam Verantwoordelijke"
                placeholder="Naam van de verantwoordelijke"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.Functie"
                label="Functie"
                placeholder="Functie"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.Certificatiestatus"
                label="Certificatiestatus"
                placeholder="Certificatiestatus"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.Adres"
                label="Straat en huisnummer"
                placeholder="Straat en huisnummer"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.PostcodeenPlaats"
                label="Postcode en Plaats"
                placeholder="Postcode en Plaats"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.Emailadres"
                label="E-mailadres"
                type="email"
                placeholder="E-mailadres"
                @update:modelValue="handleInstallatorUpdate"
              />
              <InputField
                v-model="installateurForm.Telnr"
                label="Telefoonnummer"
                type="tel"
                placeholder="Telefoonnummer"
                @update:modelValue="handleInstallatorUpdate"
              />
            </div>
            <div class="signature-section">
              <label class="input-label">Handtekening</label>
              <SignaturePad
                :model-value="handtekeningUrl ?? undefined"
                @save="handleSignature"
                @clear="clearSignature"
              />
            </div>
          </div>

          <!-- Manual Upload -->
          <div
            v-else
            class="manual-upload"
          >
            <div class="method-header">
              <span class="method-label">Handmatig Document</span>
              <button
                class="switch-method"
                @click="switchSubmissionMethod"
              >
                Overschakelen naar digitaal formulier
              </button>
            </div>
            <div class="manual-steps">
              <div class="step">
                <span class="step-number">1</span>
                <span class="step-text">Download het formulier</span>
                <Button
                  label="Download PDF"
                  icon="pi pi-download"
                  size="small"
                  severity="secondary"
                  :loading="generatingForm"
                  @click="downloadForm"
                />
              </div>
              <div class="step">
                <span class="step-number">2</span>
                <span class="step-text">Print, vul in en onderteken het formulier</span>
              </div>
              <div class="step">
                <span class="step-number">3</span>
                <span class="step-text">Upload het ingevulde formulier</span>
                <div class="upload-area">
                  <input
                    type="file"
                    @change="handleFileUpload"
                    accept="application/pdf,image/*"
                    class="file-input"
                  />
                  <div
                    v-if="uploadUrl"
                    class="uploaded-file"
                  >
                    <a
                      :href="uploadUrl"
                      target="_blank"
                      class="file-link"
                      >Bekijk geupload document</a
                    >
                    <Button
                      label="Verwijderen"
                      icon="pi pi-trash"
                      size="small"
                      severity="danger"
                      text
                      @click="confirmClearUpload"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      <!-- Submit Section -->
      <section class="detail-section submit-section">
          <Button
            :label="`Herstelverklaring ${selectedScopeLabel} indienen`"
            icon="pi pi-check"
            class="w-full"
            size="large"
            :disabled="!canSubmitScopeH.valid"
            :title="canSubmitScopeH.reason"
            @click="markScopeComplete"
          />
          <p
            v-if="autoSaveCount > 0"
            class="submit-autosave-note"
          >
            <i class="pi pi-info-circle"></i>
            U heeft {{ autoSaveCount }} niet-opgeslagen wijziging(en). Deze worden bij het indienen automatisch
            opgeslagen.
          </p>
          <p
            v-if="!canSubmitScopeH.valid && canSubmitScopeH.reason"
            class="submit-reason"
          >
            {{ canSubmitScopeH.reason }}
          </p>
          <p
            v-if="submission?.submitted_at"
            class="submit-date"
          >
            Herstelverklaring ingediend op {{ formatSubmitDate(submission.submitted_at) }}
          </p>
      </section>
    </template>
  </div>
</template>

<style scoped>
  .herstel-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  /* Highlight applied via JS to a deep-linked constatering (?focus=). */
  :deep(.constatering-focus-highlight) {
    animation: constatering-focus-pulse 2.4s ease-out;
  }

  @keyframes constatering-focus-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.5);
    }
    30% {
      box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.45);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(22, 163, 74, 0);
    }
  }

  .status-card {
    border-radius: 0.5rem;
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 2rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    display: flex;
    justify-content: center;
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .rapport-info {
    padding: 0.5rem 0;
  }

  .rapport-nr {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-900);
  }

  .detail-section {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin: 0 0 1rem;
  }

  .section-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .section-header-row .section-title {
    margin: 0;
  }

  .empty-text {
    color: var(--p-background-500);
    font-size: 0.9rem;
  }

  /* Progress Tracker */
  .progress-tracker {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .progress-item {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .progress-separator {
    color: var(--p-background-400);
    font-size: 1.25rem;
  }

  .progress-count {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--p-background-800);
  }

  .progress-label {
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .highlight {
    color: var(--color-primary, #3b82f6);
  }

  .progress-opmerking {
    color: var(--color-opmerking, #3b82f6);
  }

  .progress-afgekeurd {
    color: var(--p-red-500, #ef4444);
  }

  /* Constateringen */
  .constateringen-section {
    overflow: visible;
  }

  .constateringen-groups {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .location-group {
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
  }

  .location-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: var(--p-background-100);
    border-bottom: 1px solid var(--p-background-200);
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .location-name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-800);
  }

  .location-count {
    font-size: 0.85rem;
    font-weight: 400;
    color: var(--p-background-500);
  }

  .constateringen-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    padding: 1rem;
  }

  /* Method Selection */
  .method-selection {
    text-align: center;
    padding: 1rem 0;
  }

  .method-prompt {
    color: var(--p-background-600);
    margin-bottom: 1.5rem;
  }

  .method-buttons {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    max-width: 600px;
    margin: 0 auto;
  }

  .method-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem;
    border: 2px solid var(--p-background-200);
    border-radius: 0.5rem;
    background: var(--p-background-50);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .method-btn:hover {
    border-color: var(--p-primary-300);
    transform: translateY(-2px);
  }

  .method-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin-bottom: 0.5rem;
  }

  .method-desc {
    font-size: 0.85rem;
    color: var(--p-background-500);
    text-align: center;
  }

  /* Digital Form */
  .method-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .method-label {
    font-weight: 600;
    color: var(--p-background-700);
  }

  .switch-method {
    font-size: 0.85rem;
    color: var(--p-primary-500);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  .signature-section {
    display: flex;
    flex-direction: column;
    margin-top: 1.5rem;
    gap: 0.5rem;
  }

  /* Manual Upload */
  .manual-steps {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .step {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--p-background-50);
    border-radius: 0.5rem;
    flex-wrap: wrap;
  }

  .step-number {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--p-primary-500);
    color: white;
    border-radius: 50%;
    font-weight: 600;
    flex-shrink: 0;
  }

  .step-text {
    flex: 1;
    color: var(--p-background-700);
    min-width: 150px;
  }

  .upload-area {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    margin-top: 0.5rem;
  }

  .file-input {
    padding: 0.5rem;
    border: 1px solid var(--p-background-200);
    border-radius: 0.375rem;
    width: 100%;
  }

  .uploaded-file {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem;
    background: var(--p-background-100);
    border-radius: 0.375rem;
  }

  .file-link {
    color: var(--p-primary-500);
    text-decoration: underline;
  }

  /* Submit */
  .submit-section {
    text-align: center;
  }

  .submit-reason {
    font-size: 0.85rem;
    color: var(--p-background-500);
    margin-top: 0.75rem;
  }

  .submit-autosave-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: var(--p-amber-600, #d97706);
    margin-top: 0.75rem;
  }

  .submit-date {
    font-size: 0.9rem;
    color: var(--p-primary-500);
    font-weight: 500;
    margin-top: 0.75rem;
  }

  @media (max-width: 640px) {
    .constateringen-grid {
      grid-template-columns: 1fr;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .progress-tracker {
      gap: 0.5rem;
    }
  }
</style>
